import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { VirtualTableComponent } from '../table/virtual-table.component';
import { IColumnDefinition } from '../types';
import { AlertService } from '@omni/services/alert/alert.service';
import {
  DynamicForm,
  FormType,
} from '@omni/classes/dynamic-form/dynamic-form.class';
import { DEFAULT_FORM_LANGUAGE_CODE } from '@omni/components/shared/ind-display-form/ind-display-form.constants';
import { DynamicFormsService } from '@omni/services/dynamic-forms/dynamic-forms-service';
import { DeviceService } from '@omni/services/device/device.service';
import {
  NavigationService,
  PageName,
} from '@omni/services/navigation/navigation.service';
import { AccountOfflineService } from '@omni/services/account/account.offline.service';
import { AccountPageComponent } from '@omni/components/account/account-page/account-page';
import { AccountDataService } from '@omni/data-services/accounts/account.data.service';
import { BaseTableComponent } from '../common/base-table.component';
import { DiskService } from '@omni/services/disk/disk.service';
import { DefaultAccountDisplayForm } from '@omni/config/dynamic-forms/default-account-display';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { LocalizationService } from '@omni/services/localization/localization.service';
import { UIService } from '@omni/services/ui/ui.service';
import { NewActivityComponent } from '@omni/components/activity/new-activity/new-activity';
import { TooltipModule } from '@amin-karimi/ng2-tooltip-directive';
import { Account, AccountsModel } from '@omni/classes/account/account.class';
import { IndFilterMenuComponent } from '@omni/components/shared/ind-filter-menu/ind-filter-menu';
import { SelectedSuggestionPillDataModel } from '@omni/models/search-config-data-model';
import { AdvancedAccountSearchService } from '@omni/services/advanced-search/advanced-account-search.service';
import { AccountDragStateService } from '@omni/services/drag-drop/account-drag-state.service';
import { HapticsService } from '@omni/services/haptics/haptics.service';
import { buildRowDragGhost } from '@omni/services/drag-drop/drag-ghost.util';

@Component({
  selector: 'accounts-table',
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, VirtualTableComponent, TooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: '../common/base-table.component.html',
  styleUrl: '../common/base-table.component.scss',
  styles:
    'virtual-table { --avatar-bg-color: #DAF3DE; --avatar-text-color: #00BC1E; }',
})
export class AccountsTableComponent
  extends BaseTableComponent
  implements OnDestroy
{
  @Input() public searchText: string = '';
  @Output() public selectionChanged = new EventEmitter<Set<string>>();
  @Output() public filterChanged = new EventEmitter<
    SelectedSuggestionPillDataModel[]
  >();

  @ViewChild('configureColumnsModal') configureColumnsModal: TemplateRef<any>;
  @ViewChild('configureFilterModal') configureFilterModal: TemplateRef<any>;
  @ViewChild('configureTagsModal') configureTagsModal: TemplateRef<any>;

  private _accounts: any[] = [];
  private _selectedFilterData: SelectedSuggestionPillDataModel[] = [];

  protected readonly tableConfigKey = DB_KEY_PREFIXES.ACCOUNT_TABLE_CONFIG;
  protected readonly searchKeys = ['name'];
  private newActivityCallbackId: string;

  constructor(
    readonly disk: DiskService,
    readonly alertService: AlertService,
    public readonly accountService: AccountOfflineService,
    readonly dynamicFormService: DynamicFormsService,
    readonly translate: TranslateService,
    readonly device: DeviceService,
    readonly cdr: ChangeDetectorRef,
    readonly navService: NavigationService,
    readonly accountDataService: AccountDataService,
    readonly localizationService: LocalizationService,
    readonly uiService: UIService,
    private readonly searchService: AdvancedAccountSearchService,
    private readonly accountDragState: AccountDragStateService,
    private readonly haptics: HapticsService,
  ) {
    super(
      disk,
      alertService,
      dynamicFormService,
      translate,
      device,
      cdr,
      localizationService,
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    super.onChanges(changes);
  }

  ngOnDestroy() {
    super.onDestroy();
  }

  @HostListener('window:omni:sync:completed', ['$event'])
  @HostListener('window:omni:offlineDataLoad:Completed', ['$event'])
  syncCompleted() {
    super.onSyncCompleted();
  }

  override rowDraggable = true;

  /** Publish the dragged account on dragstart and replace the default drag
   *  preview with a card chip. Payload travels through AccountDragStateService
   *  (NOT dataTransfer) because Android System WebView strips custom MIME types
   *  and iOS WKWebView stalls resolving custom UTIs. */
  override onRowDragStart({ row, event }: { row: any; event: DragEvent }): void {
    const account: Account | undefined = row?.__account;
    if (!account) return;
    const accountName: string =
      row.name || account.accountName || 'Account';

    this.accountDragState.beginDrag({ accountId: account.id, accountName });
    this.haptics.dragReady();

    if (!event.dataTransfer) return;
    // Only set text/plain (a standard type) for desktop drag semantics and a
    // sensible label on external drops. Deliberately no custom MIME — iOS
    // WKWebView stalls ~5s resolving the custom UTI's item provider.
    event.dataTransfer.setData('text/plain', accountName);
    // 'move' (not 'copy') so the OS doesn't render a "+" cursor over the chip.
    event.dataTransfer.effectAllowed = 'move';

    const ghost = buildRowDragGhost(accountName, this.translate.instant('EMAIL_TEMPLATE_NEW_MEETING'));
    document.body.appendChild(ghost);
    event.dataTransfer.setDragImage(ghost, 16, 18);
    requestAnimationFrame(() => ghost.remove());
  }

  protected getDefaultColumns(): IColumnDefinition[] {
    return [
      {
        name: 'Name',
        field: 'name',
        width: 200,
        sticky: 'left',
        avatarField: 'avatar',
        avatarFallbackField: 'initials',
        sortable: true,
        showAlways: true,
      },
      {
        name: 'Address',
        field: 'primaryaddress',
        width: 200,
        sortable: true,
      },
    ];
  }

  getFormDefinition(): { form: DynamicForm; langCode: string } {
    let form = this.dynamicFormService.getFormDefinitionForEntity(
      'account',
      FormType.DISPLAYFORM,
    );
    let langCode = this.localizationService.selectedLanguage.localeID;
    if (!form) {
      form = new DynamicForm(DefaultAccountDisplayForm['value'][0]);
      langCode = DEFAULT_FORM_LANGUAGE_CODE;
    }
    return {
      form,
      langCode,
    };
  }

  protected async onInitData() {
    const _this = this;
    const accounts = await Promise.all(
      this.accountService.accounts.map(async (account) => ({
        ...account.raw,
        id: account.id,
        initials: account.initial,
        primaryaddress: await Promise.resolve(
          this.accountService.getPrimaryAddressOfAccount(account.id),
        ).then((address) => address?.compositeAdd),
        _image: undefined,
        __account: account,
        get avatar() {
          if (!this._image) {
            this._image = Promise.resolve(
              _this.accountDataService.getEntityImageStringForAccount(
                this.__account,
              ),
            );
          }
          return this._image;
        },
      })),
    );
    const defaultFilters = await this.searchService.initAccountSearchConfig();
    this._selectedFilterData = defaultFilters;

    await this._applyFilters(this._selectedFilterData);
  }


  private async _applyFilters(filters: SelectedSuggestionPillDataModel[]) {
    const searchResult = await this.searchService.getAccountsWithFilters(filters);
    const _this = this;
    this._accounts = await Promise.all(searchResult.value().map(async (account) => ({
        ...account.raw,
        id: account.id,
        initials: account.initial,
        primaryaddress: await Promise.resolve(
          this.accountService.getPrimaryAddressOfAccount(account.id),
        ).then((address) => address?.compositeAdd),
        _image: undefined,
        __account: account,
        get avatar() {
          if (!this._image) {
            this._image = Promise.resolve(
              _this.accountDataService.getEntityImageStringForAccount(
                this.__account,
              ),
            );
          }
          return this._image;
        },
    })));
    this._selectedFilterData = filters;
    this.filterChanged.emit(this._selectedFilterData);
  }

  protected getItems() {
    return this._accounts;
  }

  onRowClicked(row: any) {
    this.accountService.isClickedGoToAccountsTool = true;
    this.accountService.selected = row.__account;
    this.navService.pushWithPageTracking(
      AccountPageComponent,
      PageName.AccountPageComponent,
      {
        from: 'AccountTool',
        __source: 'menu',
        accountInformation: row.__account,
      },
      PageName.AccountPageComponent,
    );
  }


  async onRowAction(row: any, action: 'new-activity') {
    if (action === 'new-activity') {
      const account = row.__account;
      this.highlightedRows = account ? new Set([account.ID]) : new Set();
      this.cdr.markForCheck();
      await this.createNewActivity(account);
    }
  }

  private async createNewActivity(account: Account) {
    this.uiService.showRightPane = true;
    this.uiService.activeViewNewMeeting = 'meeting';
    this.accountService.selected = account;
    const callbackEvent = (callbackId: string) => (done, destroy) => {
      this.highlightedRows = new Set();
      if (callbackId !== this.newActivityCallbackId || done) return;
      if (!destroy) {
        this.uiService.showRightPane = false;
      }
      this.cdr.markForCheck();
    }
    this.newActivityCallbackId = new Date().getTime().toString();

    await this.navService.setChildNavRoot(
      NewActivityComponent,
      PageName.NewActivityComponent,
      PageName.AccountPageComponent,
      {
        from: 'AccountTool',
        account,
        callbackEvent: callbackEvent(this.newActivityCallbackId),
      },
    );
  }

  protected async configureFilter() {
    this.searchService.initAccountSearchConfig();
    const result = await this.alertService.showPanel({
      title: 'All Filters',
      content: {
        component: IndFilterMenuComponent,
        inputs: {
          viewData: {
            isMultipleSelectionEnabled: true,
            title: this.translate.instant('FILTERS'),
            isSearchEnabled: true,
            isReadOnly: true,
          },
          selectedFilter: this._selectedFilterData,
          from: PageName.AccountPageComponent,
          subFrom: AccountsModel.MY_ACCOUNTS_TAB
          },
      },
    });
    const data = result.data;
    if (!data?.isDone) return;
    let selectedItems = (data.selectedItems ||
      []) as SelectedSuggestionPillDataModel[];
    selectedItems.sort((a, b) => (a.createdOn < b.createdOn ? 1 : -1));
    await this.uiService.displayLoader();
    await this._applyFilters(selectedItems);
    await this.refreshData();
    await this.uiService.dismissLoader();
    this.cdr.markForCheck();
  }

  protected configureTags() {
    this.alertService.showPanel({
      title: 'All Tags',
      content: { template: this.configureTagsModal },
      buttons: [
        { key: 'cancel', label: 'Clear Tags' },
        {
          key: 'ok',
          label: 'Apply',
          primary: true,
          action: async () => {
            return true;
          },
        },
      ],
    });
  }
}
